import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ExternalLink, Send } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminBlog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pinging, setPinging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/blog`, { withCredentials: true });
      setItems(res.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load posts");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onDelete = async (slug) => {
    if (!window.confirm(`Delete "${slug}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${slug}`, { withCredentials: true });
      toast.success("Post deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onPingAll = async () => {
    setPinging(true);
    try {
      const res = await axios.post(
        `${API}/admin/indexnow/ping-all`,
        {},
        { withCredentials: true },
      );
      if (res.data.ok) {
        toast.success(`IndexNow notified — ${res.data.count} URLs submitted`);
      } else if (res.data.skipped) {
        toast.warning("IndexNow skipped", { description: res.data.error });
      } else {
        toast.error("IndexNow ping failed", { description: res.data.error || res.data.status });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "IndexNow ping failed");
    } finally { setPinging(false); }
  };

  return (
    <section data-testid="admin-blog">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h2 className="font-serif text-3xl md:text-4xl font-light tracking-tight">
          Blog posts
        </h2>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onPingAll}
            disabled={pinging}
            data-testid="admin-indexnow-ping-btn"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#0F1F4E] border border-[#D9CFA0] text-xs uppercase tracking-wider hover:bg-[#F2EBD3] disabled:opacity-60"
          >
            <Send className="w-4 h-4" /> {pinging ? "Pinging…" : "Ping IndexNow"}
          </button>
          <Link
            to="/admin/blog/new"
            data-testid="admin-blog-new-btn"
            className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#0F1F4E] text-[#F8F1D8] text-xs uppercase tracking-wider hover:bg-[#142a66]"
          >
            <Plus className="w-4 h-4" /> New post
          </Link>
        </div>
      </div>

      {loading ? (
        <p className="text-[#0F1F4E]/70">Loading…</p>
      ) : items.length === 0 ? (
        <p className="text-[#0F1F4E]/70">No posts yet.</p>
      ) : (
        <div className="overflow-x-auto bg-white border border-[#D9CFA0] rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-[#F2EBD3]">
              <tr className="text-left text-xs uppercase tracking-wider text-[#0F1F4E]/70">
                <th className="px-4 py-3">Title</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Published</th>
                <th className="px-4 py-3">Read</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr
                  key={p.slug}
                  data-testid={`admin-blog-row-${p.slug}`}
                  className="border-t border-[#D9CFA0]/70 align-top"
                >
                  <td className="px-4 py-3 font-serif text-base">{p.title}</td>
                  <td className="px-4 py-3 font-mono text-xs">{p.slug}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{p.published_at}</td>
                  <td className="px-4 py-3">{p.read_minutes} min</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-3">
                      <a
                        href={`/blog/${p.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="View"
                        className="text-[#0F1F4E]/70 hover:text-[#0F1F4E]"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <Link
                        to={`/admin/blog/${p.slug}/edit`}
                        data-testid={`admin-blog-edit-${p.slug}`}
                        title="Edit"
                        className="text-[#0F1F4E] hover:text-[#142a66]"
                      >
                        <Pencil className="w-4 h-4" />
                      </Link>
                      <button
                        type="button"
                        onClick={() => onDelete(p.slug)}
                        data-testid={`admin-blog-delete-${p.slug}`}
                        title="Delete"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
